import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { Send as SendIcon } from 'lucide-react-native';
import type { ReactNode } from 'react';
import { Send } from 'react-native-gifted-chat';

import { Box } from '@/components/ui/box';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { ERROR_MESSAGES } from '@/constants/errorMessages';
import type { useToaster } from '@/hooks/useToaster';
import { ICON_BUTTON_HIT_SLOP, MINIMUM_TARGET_SIZE } from '@/util/accessibleTouchTarget';

type ChatToolbarSendButtonProps = {
  inputToolbarProps: object;
  draftMessage: string;
  canSendWithFiles: boolean;
  isMaintenanceMode: boolean;
  isSending: boolean;
  sendingStatus: string | undefined;
  toast: ReturnType<typeof useToaster>;
  noPHIConfirmed: boolean;
  hasFiles: boolean;
  /**
   * True while a newly selected grouped tab is still resolving, when the
   * rendered conversation is still the previously selected sibling. The
   * underlying chat library clears the composer before invoking the send
   * callback, so leaving this control live would let a press wipe the draft on
   * its way to a guard that refuses to send it.
   */
  isResolvingActiveConversation?: boolean;
};

export function ChatToolbarSendButton({
  inputToolbarProps,
  draftMessage,
  canSendWithFiles,
  isMaintenanceMode,
  isSending,
  sendingStatus,
  toast,
  noPHIConfirmed,
  hasFiles,
  isResolvingActiveConversation = false,
}: ChatToolbarSendButtonProps) {
  const canSend = !!draftMessage && canSendWithFiles;

  const isSendEnabled =
    !isMaintenanceMode && !isSending && !isResolvingActiveConversation && canSend;

  const icon = (
    <Icon
      as={SendIcon}
      size="lg"
      className={isSendEnabled ? 'text-primary-500' : 'text-typography-300'}
    />
  );

  const handleDisabledPress = () => {
    if (isMaintenanceMode) {
      toast.showError(ERROR_MESSAGES.MESSAGES.MAINTENANCE_MODE);
    } else if (!draftMessage && hasFiles) {
      toast.showInfo({
        title: 'To send your attachments, please include a message',
      });
    } else if (hasFiles && !noPHIConfirmed) {
      toast.showError({
        title: 'PHI confirmation required',
        message: 'Please confirm your attachments contain no PHI before sending.',
      });
    }
  };

  let sendContent: ReactNode;

  if (isSending) {
    sendContent = (
      <Box className="flex flex-col items-center justify-center self-center">
        <Box className="flex h-10 w-10 flex-row items-center justify-center">
          <Spinner className="h-4 text-primary-500" />
        </Box>
        {sendingStatus ? (
          <Text
            {...(isWeb ? { 'aria-live': 'polite' } : { accessibilityLiveRegion: 'polite' })}
            className="sr-only absolute opacity-0"
            size="2xs">
            {sendingStatus}
          </Text>
        ) : null}
      </Box>
    );
  } else if (!isSendEnabled) {
    sendContent = (
      <Pressable
        hitSlop={ICON_BUTTON_HIT_SLOP}
        className="flex h-10 w-10 items-center justify-center active:opacity-80"
        onPress={handleDisabledPress}>
        {icon}
      </Pressable>
    );
  } else {
    sendContent = (
      <Box className="flex h-10 w-10 flex-row items-center justify-center self-center">{icon}</Box>
    );
  }

  return (
    <Box
      style={{
        flexShrink: 0,
        marginLeft: 'auto',
        width: isSending ? undefined : MINIMUM_TARGET_SIZE,
      }}>
      <Send
        {...inputToolbarProps}
        alwaysShowSend
        sendButtonProps={{
          hitSlop: ICON_BUTTON_HIT_SLOP,
          activeOpacity: 0.8,
          testID: 'chat-send-button',
        }}
        containerStyle={{
          alignSelf: 'center',
          alignItems: 'flex-end',
          flexGrow: 0,
          flexShrink: 0,
          justifyContent: 'center',
          margin: 0,
          minWidth: MINIMUM_TARGET_SIZE,
          minHeight: MINIMUM_TARGET_SIZE,
          width: isSending ? undefined : MINIMUM_TARGET_SIZE,
        }}
        disabled={!isSendEnabled}>
        {sendContent}
      </Send>
    </Box>
  );
}
